import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
} from "@mui/material";

import { paths } from "../../../routes/paths";

import { getAllJobs, getAdminCompanies } from "../../../services/jobService";
import { AdminPage } from "./AdminShell";
import AdminFilterBar, { companyOptions } from "./AdminFilterBar";
import useAdminFilters from "./useAdminFilters";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

const LIMIT = 25;

/** Cross-company job listing — every job on the platform. Super-admin only. */
const AllJobs = () => {
  const navigate = useNavigate();
  const { filters, searchText, setSearch, setFilter, clearAll, page, setPage, hasFilters } =
    useAdminFilters(["q", "company_id", "owner_id"]);

  const [data, setData] = useState({ jobs: [], total_jobs: 0, applied_filters: {} });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAdminCompanies().then(setCompanies);
  }, []);

  useEffect(() => {
    let cancelled = false;   // also the stale-response guard: a slow "jav" can't overwrite "java"
    setLoading(true);
    setError(null);
    getAllJobs({ page, limit: LIMIT, ...filters })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.q, filters.company_id, filters.owner_id]);

  const owner = data.applied_filters?.owner;

  return (
    <AdminPage title="All Jobs" subtitle={`${data.total_jobs} across all companies.`}>
      <AdminFilterBar
        searchPlaceholder="Search jobs, owners, companies…"
        searchValue={searchText}
        onSearchChange={setSearch}
        hasFilters={hasFilters}
        onClear={clearAll}
        dropdowns={[
          {
            key: "company_id",
            label: "Company",
            value: filters.company_id,
            onChange: (value) => setFilter("company_id", value),
            options: companyOptions(companies, filters.company_id),
          },
        ]}
        chips={
          filters.owner_id
            ? [
                {
                  key: "owner",
                  // The name comes from the server's applied_filters echo, so it isn't there on
                  // the first paint. Show nothing rather than "unknown user" — that label is
                  // reserved for an owner_id the server looked up and genuinely could not find.
                  label: `Owner: ${
                    loading && !owner ? "…" : owner?.name || owner?.email || "unknown user"
                  }`,
                  onDelete: () => setFilter("owner_id", ""),
                },
              ]
            : []
        }
      />
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 5, textAlign: "center", font: `400 13px ${font.sans}`, color: color.danger }}>
          {error}
        </Box>
      ) : !data.jobs.length ? (
        <Box sx={{ py: 5, textAlign: "center", font: `400 13px ${font.sans}`, color: color.textSecondary }}>
          {hasFilters ? "No jobs match your filters." : "No jobs found."}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: `${tokens.radius.card}px`,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Candidates</TableCell>
                <TableCell>Posted</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.jobs.map((job) => (
                <TableRow
                  key={job.job_id}
                  hover
                  onClick={() => navigate(`${paths.v1.jobs}/${job.job_id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Box sx={{ font: `500 13px ${font.sans}`, color: color.text }}>
                      {job.job_title || "Untitled"}
                    </Box>
                    <Box sx={{ font: `400 11px ${font.sans}`, color: color.textMuted }}>
                      {job.location || "—"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {/* owner is null for orphaned jobs — the outer join surfaces them on purpose */}
                    {job.owner ? (
                      <>
                        <Box sx={{ font: `500 13px ${font.sans}`, color: color.text }}>
                          {job.owner?.name || "—"}
                        </Box>
                        <Box sx={{ font: `400 11px ${font.sans}`, color: color.textMuted }}>
                          {job.owner?.email}
                        </Box>
                      </>
                    ) : (
                      <Chip size="small" label="no owner" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {job.company?.name || job.company?.email_domain || (
                      <Chip size="small" label="no company" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ font: `500 13px ${font.sans}`, color: color.text }}>
                      {job.candidate_count ?? 0}
                    </Box>
                    <Box sx={{ font: `400 11px ${font.sans}`, color: color.textMuted }}>
                      {job.shortlist_count ? `${job.shortlist_count} shortlisted` : "—"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {job.date_posted ? new Date(job.date_posted).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={job.job_open ? "Open" : "Closed"}
                      color={job.job_open ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={data.total_jobs}
            page={page - 1}
            onPageChange={(_e, p) => setPage(p + 1)}
            rowsPerPage={LIMIT}
            rowsPerPageOptions={[LIMIT]}
          />
        </Box>
      )}
    </AdminPage>
  );
};

export default AllJobs;
